import { get, writable } from "svelte/store";
import { response } from "@stores/global";

const store = writable();

/**
 * Create a readonly feature flag store
 *
 * Example experience data:
 *
 * {
 *   ...
 *   features: [
 *     {
 *       type: 'suggested-questions',
 *       enabled: true
 *     },
 *     {
 *       type: 'something-experimental',
 *       enabled: false
 *     },
 *     {
 *       type: 'something-with-configuration',
 *       enabled: true,
 *       properties: {
 *         someProperty: 1
 *       }
 *     }
 *     ...
 *   ]
 * }
 *
 * @param {Array<object>} featureConfigurations
 * @returns {object} store value
 */
export default function createStore(featureConfigurations = []) {
  if (typeof get(store) !== "undefined") {
    return get(store);
  }

  const features = {};

  featureConfigurations.forEach((feature) => {
    features[feature.type] = feature;
  });

  store.set(features);

  return get(store);
}

/**
 * Add a feature to the store,
 * e.g. when returned by an intent
 *
 * @param {object} feature
 */
export function addFeature(feature) {
  if (typeof feature.type !== "string") {
    throw new Error("Expected a `type` string");
  }

  store.update((features) => {
    features[feature.type] = feature;
    return features;
  });
}

/**
 * Is a given feature enabled. Returns false if feature
 * does not exist.
 *
 * @param {string} type
 * @returns {boolean}
 */
export function featureEnabled(type) {
  if (typeof type !== "string") {
    throw new Error("Expected a `type` string");
  }

  const feature = (get(store) || {})[type];

  if (!feature) {
    return false;
  }

  return Boolean(feature.enabled);
}

/**
 * Get response payload for a given feature.
 * Returns null if feature is not enabled.
 *
 * @param {string} type
 * @returns {object}
 */
export function featurePayload(type) {
  if (typeof type !== "string") {
    throw new Error("Expected a `type` string");
  }

  const feature = (get(store) || {})[type];

  if (!feature || !feature.enabled) {
    return null;
  }

  const responseFeature =
    get(response) &&
    get(response).features &&
    get(response).features.find((f) => f.type === type);

  if (
    !responseFeature ||
    !responseFeature.payload ||
    !Object.values(responseFeature.payload).length
  ) {
    return null;
  }

  return responseFeature.payload;
}

/**
 * Get configuration properties for a given feature.
 * Returns null if feature is not enabled.
 *
 * @param {string} type
 * @returns {object}
 */
export function featureProperties(type) {
  if (typeof type !== "string") {
    throw new Error("Expected a `type` string");
  }

  const feature = (get(store) || {})[type];

  if (!feature || !feature.enabled) {
    return null;
  }

  return feature.properties || {};
}
